import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAppConfig } from './AppConfigContext';

/**
 * ------------------------------------------------------------------
 * INDICE DE FUNCIONES Y ESTRUCTURA
 * ------------------------------------------------------------------
 * 1. Context Creation .................... Creación del contexto
 * 2. Custom Hook (useAlert) .............. Hook para usar el contexto
 * 3. Provider (AlertProvider) ............ Componente proveedor
 *    - State ............................. Estados para alertas y toasts
 *    - showAlert ......................... Mostrar alerta de confirmación
 *    - hideAlert ......................... Ocultar alerta
 *    - showToast ......................... Mostrar notificación toast
 *    - hideToast ......................... Ocultar notificación toast
 * ------------------------------------------------------------------
 */

/**
 * ------------------------------------------------------------------
 * 1. Context Creation
 * ------------------------------------------------------------------
 */
const AlertContext = createContext();

/**
 * ------------------------------------------------------------------
 * 2. Custom Hook (useAlert)
 * ------------------------------------------------------------------
 */
export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

/**
 * ------------------------------------------------------------------
 * 3. Provider (AlertProvider)
 * ------------------------------------------------------------------
 */
export const AlertProvider = ({ children }) => {
    const { config } = useAppConfig();

    // State
    const [alertState, setAlertState] = useState({
        show: false,
        title: '',
        message: '',
        alertVariant: 'warning',
        confirmLabel: 'Confirmar',
        cancelLabel: 'Cancelar',
        confirmVariant: 'danger',
        onConfirm: null,
    });

    // Toast notification state
    const [toastState, setToastState] = useState({
        show: false,
        message: '',
        variant: 'danger',
        details: null
    });

    // Auto-dismiss toast logic
    React.useEffect(() => {
        if (toastState.show) {
            const timer = setTimeout(() => {
                setToastState(prev => ({ ...prev, show: false }));
            }, 3000); // 3 segundos
            return () => clearTimeout(timer);
        }
    }, [toastState.show]);

    // showAlert
    const showAlert = useCallback((options) => {
        setAlertState({
            show: true,
            title: options.title || 'Confirmar',
            message: options.message || '',
            alertVariant: options.alertVariant || 'warning',
            confirmLabel: options.confirmLabel || 'Confirmar',
            cancelLabel: options.cancelLabel || 'Cancelar',
            confirmVariant: options.confirmVariant || 'danger',
            onConfirm: options.onConfirm,
        });
    }, []);

    // hideAlert
    const hideAlert = useCallback(() => {
        setAlertState((prev) => ({ ...prev, show: false }));
    }, []);

    // showToast
    const showToast = useCallback((message, variant = 'danger', details = null) => {
        // Verificar configuración global de notificaciones
        if (config.showNotifications === false) return;

        setToastState({
            show: true,
            message,
            variant,
            details
        });
    }, [config.showNotifications]);

    // hideToast
    const hideToast = useCallback((prev) => {
        setToastState((prev) => ({ ...prev, show: false }));
    }, []);

    return (
        <AlertContext.Provider value={{ showAlert, hideAlert, alertState, showToast, hideToast, toastState }}>
            {children}
        </AlertContext.Provider>
    );
};
